import {
  useEffect,
  useId,
  useRef,
  useState,
  type FormEvent,
} from "react";
import { CircleCheck, Paperclip, Send, Share2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery } from "@apollo/client/react";

import { Alert } from "@lytra/frontend-common/components/v2/ui/alert";
import { Button } from "@lytra/frontend-common/components/v2/ui/button";
import { Checkbox } from "@lytra/frontend-common/components/v2/ui/checkbox";
import { Field } from "@lytra/frontend-common/components/v2/ui/field";
import { Icon } from "@lytra/frontend-common/components/v2/ui/icon";
import { Switch } from "@lytra/frontend-common/components/v2/ui/switch";
import { Textarea } from "@lytra/frontend-common/components/v2/ui/textarea";
import { useSendSignatureHtml } from "@/hooks/useSendSignatureHtml";
import { useProfile } from "@/providers/ProfileProvider";
import { cn } from "@/lib/utils";
import type { PickerAgent } from "./MentionList";
import { RecipientField } from "./RecipientField";
import {
  addRecipient,
  channelSummary,
  classifyRecipient,
  isAddressShaped,
  toRecipientInputs,
  type AgentsState,
} from "./recipients";
import {
  TICKET_FORWARD_TENANT_DOMAINS,
  TICKET_FORWARD_TO_COLLEAGUES,
} from "./forwardQueries";
import {
  afterForwardRefusal,
  forwardErrorKind,
  forwardTextLength,
  keyForForwardSubmit,
  MAX_FORWARD_TEXT_CHARS,
  type ForwardDraft,
  type ForwardDrafts,
  type ForwardErrorKind,
} from "./forwardDraft";
import type { TicketMessage } from "./noteThreads";

/** What the composer reads of a message: its id, subject and attachments. */
export type ForwardMessage = Pick<
  TicketMessage,
  "id" | "direction" | "emailSubject" | "contact" | "agent" | "attachments"
>;

/** The counter under the message shows from here on; the limit itself is MAX_FORWARD_TEXT_CHARS. */
const TEXT_COUNTER_FROM = 4000;

const SIZE_FORMAT = new Intl.NumberFormat("de-DE", {
  maximumFractionDigits: 1,
});

/** "840 B", "320 KB", "1,4 MB": binary steps like the attachment list of the ticket. */
function formatSize(bytes: number): string {
  if (bytes < 1024) return `${SIZE_FORMAT.format(bytes)} B`;
  if (bytes < 1024 * 1024)
    return `${SIZE_FORMAT.format(Math.round(bytes / 1024))} KB`;
  return `${SIZE_FORMAT.format(bytes / (1024 * 1024))} MB`;
}

/** The subject the mail will carry: "WG: " plus the original without line breaks (as the server builds it, D-08). */
function forwardSubject(original: string | null | undefined): string | null {
  const cleaned = (original ?? "").replace(/[\r\n]/g, "").trim();
  return cleaned === "" ? null : cleaned;
}

export interface ForwardComposerProps {
  message: ForwardMessage;
  conversationId: string;
  /** The drafts of the ticket view, kept by the host so a closed composer keeps its draft. */
  drafts: ForwardDrafts;
  /** False while sending by mail is off: colleagues only, no mail copy. */
  mailEnabled: boolean;
  /** The tenant's colleagues (the `agents` query of the ticket). */
  agents: readonly PickerAgent[];
  agentsState: AgentsState;
  /** Closes the composer (Abbrechen, Escape, or after a send). */
  onClose: () => void;
  /** Called once after a successful send with the number of recipients. */
  onSent: (count: number) => void;
}

/**
 * "Mit Kollegen teilen" (#717, D-05 to D-10): the inline card at the foot of the thread that
 * composes the recipient field with the message and sends it through `forwardToColleagues`.
 * It takes the place of its trigger, like the note composer. The host mounts it only while
 * open; the draft (recipients, text, the attachment and internal choices, key) lives in the
 * host's `drafts`.
 * While a send is in flight it cannot be closed, so the payload cannot drift from the key
 * being sent (T-03-32).
 */
export function ForwardComposer({
  message,
  conversationId,
  drafts,
  mailEnabled,
  agents,
  agentsState,
  onClose,
  onSent,
}: ForwardComposerProps) {
  const { t } = useTranslation();
  const [pending, setPending] = useState(false);
  const titleId = useId();
  return (
    <section
      data-slot="forward-composer"
      aria-labelledby={titleId}
      className="flex w-full max-w-[65ch] min-w-0 flex-col gap-3 rounded-xl border border-dashed border-primary-line bg-card p-4"
      onKeyDown={(event) => {
        // The recipient field's picker takes the first Escape (and stops it); the next closes.
        if (event.key !== "Escape" || event.defaultPrevented || pending) return;
        event.preventDefault();
        onClose();
      }}
    >
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <Icon icon={Share2} className="text-primary-accent" />
          <h3 id={titleId} className="text-label text-ink">
            {t("tickets.forward.title")}
          </h3>
        </div>
        <p className="text-caption text-ink-muted">
          {t(
            mailEnabled
              ? "tickets.forward.description"
              : "tickets.forward.descriptionLytraOnly",
          )}
        </p>
      </div>
      <ForwardForm
        key={message.id}
        message={message}
        conversationId={conversationId}
        drafts={drafts}
        pending={pending}
        onPendingChange={setPending}
        onClose={onClose}
        agents={agents}
        agentsState={agentsState}
        mailEnabled={mailEnabled}
        onSent={onSent}
      />
    </section>
  );
}

interface ForwardFormProps {
  message: ForwardMessage;
  conversationId: string;
  drafts: ForwardDrafts;
  pending: boolean;
  onPendingChange: (pending: boolean) => void;
  /** Closes the composer for good, also while `pending` has just ended. */
  onClose: () => void;
  agents: readonly PickerAgent[];
  agentsState: AgentsState;
  /** False while sending by mail is off: colleagues only, no mail copy. */
  mailEnabled: boolean;
  onSent: (count: number) => void;
}

function ForwardForm({
  message,
  conversationId,
  drafts,
  pending,
  onPendingChange,
  onClose,
  agents,
  agentsState,
  mailEnabled,
  onSent,
}: ForwardFormProps) {
  const { t } = useTranslation();
  const { user } = useProfile();
  // The mail ends with the sender's signature exactly as a direct reply would.
  const signatureHtml = useSendSignatureHtml();
  const { data: tenantData } = useQuery(TICKET_FORWARD_TENANT_DOMAINS, {
    errorPolicy: "all",
  });
  const [forward] = useMutation(TICKET_FORWARD_TO_COLLEAGUES, {
    errorPolicy: "none",
    refetchQueries: ["Ticket"],
  });

  // The composer is unmounted while closed: reopening on this message restores the draft
  // (recipients, text, the attachment and internal choices, key), another message starts empty.
  const [draft, setDraftState] = useState<ForwardDraft>(() =>
    drafts.get(message.id),
  );
  const draftRef = useRef(draft);
  const setDraft = (next: ForwardDraft) => {
    draftRef.current = next;
    setDraftState(next);
    drafts.set(message.id, next);
  };
  const {
    recipients,
    text,
    includeAttachments,
    includeInternalMessages,
    includeInternalNotes,
  } = draft;
  const textLength = forwardTextLength(text);
  const [recipientError, setRecipientError] = useState<string | null>(null);
  const [textError, setTextError] = useState<string | null>(null);
  const [sendError, setSendError] = useState<ForwardErrorKind | null>(null);
  const sendingRef = useRef(false);
  const recipientInputRef = useRef<HTMLInputElement>(null);
  const textRef = useRef<HTMLTextAreaElement>(null);
  const attachmentsRef = useRef<HTMLButtonElement>(null);
  const summaryId = useId();
  const attachmentsLabelId = useId();
  const attachmentsSizeId = useId();
  const internalMessagesLabelId = useId();
  const internalNotesLabelId = useId();

  // Opened from its trigger: the recipient input takes the focus (and brings the card into view).
  useEffect(() => {
    recipientInputRef.current?.focus();
  }, []);

  // After a "too large" refusal the way out is the attachment checkbox; it is read-only while
  // the send is in flight, so the focus moves once the fields are editable again.
  useEffect(() => {
    if (sendError === "tooLarge" && !pending) attachmentsRef.current?.focus();
  }, [sendError, pending]);

  const internalDomains = tenantData?.tenant?.internalDomains ?? null;
  const counts = channelSummary(
    recipients.map((entry) =>
      classifyRecipient(entry, agents, agentsState, internalDomains),
    ),
  );
  const chips = counts.lytra + counts.mail + counts.unknown;
  // Mail-only parts are removed, not disabled, while no chip leaves lytra (UI-SPEC 2), and
  // always while sending by mail is off.
  const mailBlocks = mailEnabled && (counts.mail > 0 || counts.unknown > 0);
  const lytraOnly = chips > 0 && !mailBlocks;

  // The summary weighs lytra against mail: with mail off there is nothing to weigh.
  const summary =
    chips === 0 || !mailEnabled
      ? null
      : counts.unknown > 0
        ? t("tickets.forward.summary.unknown")
        : counts.mail === 0
          ? t("tickets.forward.summary.lytraOnly")
          : counts.lytra === 0
            ? t("tickets.forward.summary.mailOnly")
            : t("tickets.forward.summary.mixed", {
                lytra: counts.lytra,
                mail: counts.mail,
              });

  const files = message.attachments.edges
    .map((edge) => edge.node)
    .filter((attachment) => !attachment.isInline);
  const totalSize = formatSize(
    files.reduce((sum, file) => sum + (file.size ?? 0), 0),
  );
  const subject = forwardSubject(message.emailSubject);
  const subjectLine = t("tickets.forward.subject.line", {
    value: subject
      ? t("tickets.forward.subject.value", { subject })
      : t("tickets.forward.subject.none"),
  });

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (sendingRef.current) return;

    // Validation runs on submit only. Blur has already turned valid text in the recipient input
    // into a chip; what is left there is invalid, unless nothing blurred it (then it is added
    // here, nothing typed is silently dropped).
    let list = draftRef.current.recipients;
    const leftover = recipientInputRef.current?.value.trim() ?? "";
    if (leftover !== "") {
      const added = isAddressShaped(leftover)
        ? addRecipient(
            list,
            { address: leftover },
            {
              viewerId: user.id,
              viewerEmail: user.emailAddress,
              agents,
              mailEnabled,
            },
          )
        : null;
      if (!added || added.error) {
        setRecipientError(
          added?.error === "self"
            ? t("tickets.forward.errors.self")
            : added?.error === "cap"
              ? t("tickets.forward.errors.cap")
              : added?.error === "noAccount"
                ? t("tickets.forward.errors.noAccount", { input: leftover })
                : t("tickets.forward.errors.invalidAddress", {
                    input: leftover,
                  }),
        );
        recipientInputRef.current?.focus();
        return;
      }
      list = added.list;
    }
    if (list.length === 0) {
      setRecipientError(t("tickets.forward.errors.noRecipient"));
      recipientInputRef.current?.focus();
      return;
    }
    if (forwardTextLength(draftRef.current.text) > MAX_FORWARD_TEXT_CHARS) {
      setTextError(t("tickets.forward.errors.tooLong"));
      textRef.current?.focus();
      return;
    }
    setRecipientError(null);
    setTextError(null);

    // The key comes from the draft, never from the click: an unchanged retry is the same send.
    const { clientRequestId, draft: keyed } = keyForForwardSubmit({
      ...draftRef.current,
      recipients: list,
    });
    setDraft(keyed);

    sendingRef.current = true;
    onPendingChange(true);
    setSendError(null);
    try {
      await forward({
        variables: {
          conversationId,
          messageId: message.id,
          recipients: toRecipientInputs(keyed.recipients, agents),
          clientRequestId,
          text: keyed.text,
          includeAttachments: keyed.includeAttachments,
          includeInternalMessages: keyed.includeInternalMessages,
          includeInternalNotes: keyed.includeInternalNotes,
          signatureHtml,
        },
      });
    } catch (error) {
      // Every input stays; the payload is remembered so only a change mints a new key (a
      // reused-key refusal mints one right away, the old key can never succeed).
      const kind = forwardErrorKind(error);
      setDraft(afterForwardRefusal(draftRef.current, kind));
      setSendError(kind);
      return;
    } finally {
      sendingRef.current = false;
      onPendingChange(false);
    }
    drafts.clear(message.id);
    onClose();
    onSent(keyed.recipients.length);
  };

  return (
    <form
      className="flex min-w-0 flex-col gap-3"
      onSubmit={(event) => void submit(event)}
      onKeyDown={(event) => {
        // Enter in the recipient field adds a recipient (or does nothing); it never sends.
        if (event.key === "Enter" && event.target === recipientInputRef.current) {
          event.preventDefault();
        }
      }}
    >
      <div className="flex flex-col gap-1">
        <Field
          label={t("tickets.forward.recipients.label")}
          error={recipientError}
        >
          <RecipientField
            value={recipients}
            onChange={(next) => setDraft({ ...draftRef.current, recipients: next })}
            agents={agents}
            agentsState={agentsState}
            internalDomains={internalDomains}
            viewerId={user.id}
            viewerEmail={user.emailAddress}
            error={recipientError}
            onError={setRecipientError}
            inputRef={recipientInputRef}
            disabled={pending}
            describedById={summary ? summaryId : undefined}
            mailEnabled={mailEnabled}
          />
        </Field>
        {summary && (
          <p
            id={summaryId}
            aria-live="polite"
            className="flex items-start gap-1 text-caption text-ink-muted"
          >
            <span className="mt-0.5 flex shrink-0">
              <Icon icon={CircleCheck} size={12} className="text-primary-accent" />
            </span>
            <span className="min-w-0">{summary}</span>
          </p>
        )}
      </div>

      {/* While mail is on, always there, so they are found before anyone is added; with mail
          off there is no mail and no thread for them to shape. */}
      {mailEnabled && (
        <div className="flex min-w-0 flex-col gap-1.5">
          <label className="flex w-fit max-w-full min-w-0 cursor-pointer items-center gap-2">
            <Switch
              aria-labelledby={internalMessagesLabelId}
              checked={includeInternalMessages}
              disabled={pending}
              onCheckedChange={(next) =>
                setDraft({ ...draftRef.current, includeInternalMessages: next })
              }
            />
            <span
              id={internalMessagesLabelId}
              className="min-w-0 text-caption text-ink"
            >
              {t("tickets.forward.internal.messages")}
            </span>
          </label>
          <label className="flex w-fit max-w-full min-w-0 cursor-pointer items-center gap-2">
            <Switch
              aria-labelledby={internalNotesLabelId}
              checked={includeInternalNotes}
              disabled={pending}
              onCheckedChange={(next) =>
                setDraft({ ...draftRef.current, includeInternalNotes: next })
              }
            />
            <span
              id={internalNotesLabelId}
              className="min-w-0 text-caption text-ink"
            >
              {t("tickets.forward.internal.notes")}
            </span>
          </label>
        </div>
      )}

      <Field
        label={
          <>
            {t("tickets.forward.message.label")}{" "}
            <span className="font-normal text-ink-muted">
              {t("tickets.forward.message.hint")}
            </span>
          </>
        }
        error={textError}
        count={textLength >= TEXT_COUNTER_FROM ? textLength : undefined}
        maxCount={MAX_FORWARD_TEXT_CHARS}
      >
        <Textarea
          ref={textRef}
          value={text}
          readOnly={pending}
          placeholder={t("tickets.forward.message.placeholder")}
          onChange={(event) => {
            if (textError) setTextError(null);
            setDraft({ ...draftRef.current, text: event.target.value });
          }}
          className="min-h-20"
        />
      </Field>

      {files.length > 0 && mailBlocks && (
        // The one choice left about what goes along, drawn as a chip; the link, the forwarded
        // message and the signature always go with the mail.
        <label
          className={cn(
            "flex w-fit max-w-full min-w-0 cursor-pointer items-center gap-1.5 rounded-md border px-2 py-1 text-caption font-semibold",
            includeAttachments
              ? "border-primary-line bg-primary-subtle text-primary-strong"
              : "border-border text-ink-muted",
          )}
        >
          <Checkbox
            ref={attachmentsRef}
            aria-labelledby={attachmentsLabelId}
            aria-describedby={attachmentsSizeId}
            checked={includeAttachments}
            disabled={pending}
            onCheckedChange={(next) =>
              setDraft({
                ...draftRef.current,
                includeAttachments: next === true,
              })
            }
          />
          <Icon icon={Paperclip} size={12} />
          <span id={attachmentsLabelId}>
            {t("tickets.forward.attachments.label")}
          </span>
          <span id={attachmentsSizeId} className="font-normal text-ink-muted">
            {t(
              files.length === 1
                ? "tickets.forward.attachments.hintOne"
                : "tickets.forward.attachments.hintOther",
              { count: files.length, size: totalSize },
            )}
          </span>
        </label>
      )}
      {files.length > 0 && lytraOnly && (
        <p className="text-caption text-ink-muted">
          {t("tickets.forward.attachments.lytraOnly")}
        </p>
      )}
      {sendError && (
        <Alert tone="error">
          {t(
            sendError === "gone"
              ? "tickets.forward.errors.gone"
              : sendError === "tooLarge"
                ? "tickets.forward.errors.tooLarge"
                : sendError === "keyReused"
                  ? "tickets.forward.errors.changed"
                  : sendError === "mailUnavailable"
                    ? "tickets.forward.errors.mailUnavailable"
                    : "tickets.forward.errors.sendFailed",
          )}
        </Alert>
      )}
      <div className="flex min-w-0 items-center gap-2">
        {mailBlocks && (
          <span
            className="min-w-0 flex-1 truncate text-caption text-ink-muted"
            title={subjectLine}
          >
            {subjectLine}
          </span>
        )}
        <Button
          type="button"
          variant="ghost"
          size="md"
          className={mailBlocks ? undefined : "ml-auto"}
          disabled={pending}
          onClick={onClose}
        >
          {t("tickets.forward.actions.cancel")}
        </Button>
        <Button type="submit" size="md" leadingIcon={Send} loading={pending}>
          {t("tickets.forward.actions.send")}
        </Button>
      </div>
    </form>
  );
}
